import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Alert, Spinner } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import MainLayout from '../../Components/Layout/MainLayout/MainLayout';
import { useAuth } from '../../Components/AuthContext';

const BestAllelePage = () => {
  const { tenantDomain } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [userObj, setUserObj] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    try {
      if (!user) return;
      const parsed = typeof user === 'string' ? JSON.parse(user) : user;
      setUserObj(parsed);
      if (parsed.tenantDomain !== tenantDomain) {
        navigate(`/${tenantDomain}/login`, {
          state: { error: 'Access denied. Please login to the correct tenant.' }
        });
      }
    } catch (err) {
      console.error('Error parsing JWT:', err);
      navigate(`/${tenantDomain}/login`);
    }
  }, [tenantDomain, navigate, user]);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && (file.name.toLowerCase().endsWith('.csv') ||
                 file.type === 'text/csv' ||
                 file.type === 'application/csv' ||
                 file.type === 'text/plain')) {
      setSelectedFile(file);
      setError(null);
    } else {
      setError('Please select a valid CSV file');
      setSelectedFile(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file first');
      return;
    }

    setIsUploading(true);
    setError(null);
    setResults(null);

    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/${tenantDomain}/appm/predict-best`, {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = 'best_allele_predictions.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      setResults({ message: 'Analysis complete! Excel file downloaded with the best HLA allele per peptide.' });
    } catch (err) {
      setError('Error uploading file: ' + err.message);
    } finally {
      setIsUploading(false);
    }
  };

  if (!userObj) return null;

  return (
    <div className="d-flex">
      <MainLayout>
        <Container fluid className="p-4">
          <Row className="mb-4">
            <Col>
              <h2>Best HLA Allele Finder</h2>
              <p className="text-muted">
                Upload a CSV of peptide sequences and we'll determine the HLA allele most likely to bind each one across all 20 supported alleles.
              </p>
              <div className="bg-light p-3 rounded border-start border-primary border-4 mb-3">
                <h6 className="mb-2">
                  <i className="fas fa-graduation-cap me-2 text-primary"></i>
                  Research Citation
                </h6>
                <p className="mb-2 small">
                  This tool implements the APPM (Antigen Presentation Prediction Model) neural network from:
                </p>
                <p className="mb-1 small">
                  <strong>He H, Xu H, Zhang Y, et al.</strong> APPM: Antigen presentation prediction model based on convolutional neural network.{' '}
                  <em>Comput Struct Biotechnol J</em>. 2021;19:2647-2656.{' '}
                  <a
                    href="https://pmc.ncbi.nlm.nih.gov/articles/PMC8186784/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-decoration-none ms-1"
                  >
                    <i className="fas fa-external-link-alt"></i> View Paper
                  </a>
                </p>
                <p className="mb-0 small">
                  <strong>Source Code:</strong>{' '}
                  <a
                    href="https://github.com/haoqing12/APPM"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-decoration-none ms-1"
                  >
                    <i className="fab fa-github"></i> GitHub Repository
                  </a>
                </p>
              </div>
            </Col>
          </Row>

          {error && (
            <Row className="mb-4">
              <Col>
                <Alert variant="danger">{error}</Alert>
              </Col>
            </Row>
          )}

          <Row className="mb-4">
            <Col>
              <Card className="h-100">
                <Card.Header className="bg-success text-white">
                  <h4 className="mb-0">Upload Peptides — Find Best HLA Allele</h4>
                </Card.Header>
                <Card.Body className="p-5">
                  <Row className="align-items-center" style={{ minHeight: '300px' }}>
                    <Col md={8}>
                      <div className="mb-4">
                        <label htmlFor="csvFile" className="form-label h5">Select your CSV file:</label>
                        <input
                          id="csvFile"
                          type="file"
                          accept=".csv"
                          onChange={handleFileChange}
                          className="form-control form-control-lg"
                          disabled={isUploading}
                        />
                      </div>
                      <div className="mb-4 p-3 bg-light rounded">
                        <h6 className="mb-2">File Format Requirements:</h6>
                        <ul className="mb-2">
                          <li>CSV file with a <strong>"Peptide"</strong> column</li>
                          <li>Each row should contain one peptide sequence (8–11 amino acids)</li>
                          <li>Invalid amino acids (X, B, U) will be filtered out automatically</li>
                          <li>Example format:</li>
                        </ul>
                        <div className="bg-white p-2 rounded border">
                          <code>
                            Peptide<br/>
                            EGWLHKRGK<br/>
                            GWLHKRGKY<br/>
                            KLLNSMVTI
                          </code>
                        </div>
                      </div>
                      <div className="mb-4 p-3 bg-info bg-opacity-10 border border-info rounded">
                        <h6 className="text-info mb-1">How it works:</h6>
                        <p className="mb-0 small">
                          Each peptide is scored against all 20 HLA alleles (A0101, A0201, A0203, A0207, A0301, A1101, A2402, A2902, A3101, A3201, A6802, B0702, B1501, B3501, B4001, B4402, B4403, B5101, B5401, B5701).
                          The output Excel file will show the best-matching allele and its binding score for each peptide.
                        </p>
                      </div>
                      {selectedFile && (
                        <div className="mb-4 p-3 bg-success bg-opacity-10 border border-success rounded">
                          <h6 className="text-success mb-1">File Selected:</h6>
                          <p className="mb-0"><strong>{selectedFile.name}</strong> ({Math.round(selectedFile.size / 1024)} KB)</p>
                        </div>
                      )}
                    </Col>
                    <Col md={4} className="text-center">
                      <Button
                        onClick={handleUpload}
                        disabled={!selectedFile || isUploading}
                        variant="success"
                        size="lg"
                        className="px-5 py-3"
                      >
                        {isUploading ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Analyzing all alleles...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-search me-2"></i>
                            Find Best Allele
                          </>
                        )}
                      </Button>
                      {isUploading && (
                        <p className="text-muted small mt-3">
                          Running all 20 allele models — this may take a minute.
                        </p>
                      )}
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {results && (
            <Row>
              <Col>
                <Alert variant="success" className="d-flex align-items-center">
                  <i className="fas fa-check-circle me-2"></i>
                  {results.message}
                </Alert>
              </Col>
            </Row>
          )}
        </Container>
      </MainLayout>
    </div>
  );
};

export default BestAllelePage;
